// Imported (not re-declared) so this file's own annotations and the re-export below both
// refer to the one declaration in @models/paymentMethod — see that file.
import { PaymentMethodType } from "./paymentMethod";
import { INSUFFICIENT_STOCK } from "./shoppingCart";
import { parseJsonObject } from "@utils/apiFailure";

// Every order carries exactly one payment instrument (#429): a vendor nonce OR a saved bank
// profile. Never neither — an order without one is refused (400), and checkout never offers
// an unpaid order — and never both (also 400). The union is what keeps a request without an
// instrument, or with two, from type-checking: each member declares the OTHER credential as
// `?: never`, because TypeScript only rejects an excess property on a union when no member
// declares it. paymentMethod (CONFIRMED #296 against API main @ 386f687, PR 242 —
// CreateOrderCommand/CreateOrderValidator) is tied to the credential: a nonce is a card or a
// bank account; a saved profile is bank-only (the API 400s Card with a saved profile).
export type CreateOrderRequest =
    | {
        locationId: string;
        paymentMethod: PaymentMethodType.Card | PaymentMethodType.BankAccount;
        // Opaque Authorize.Net Accept nonce ("<dataDescriptor>|<dataValue>"). When the
        // charge is declined the API responds 402 and the order is not created. Since #296
        // this carries either a card nonce (AcceptUI) or a bank-account nonce (Accept.js) —
        // paymentMethod says which.
        vendorPaymentToken: string;
        paymentProfileId?: never;
    }
    | {
        locationId: string;
        paymentMethod: PaymentMethodType.BankAccount;
        // The API's Guid id of the org's ACTIVE stored BANK profile (@models/bankAccount —
        // NOT the vendor CIM id, which never reaches the browser). Saved-card checkout does
        // not exist; cards always re-tokenize. CONFIRMED (#296), as above.
        paymentProfileId: string;
        vendorPaymentToken?: never;
    };

export interface CreateOrderResponse {
    id: string;
}

// Machine-readable `error` code of the contracted POST /orders 500 failure body
// ({ error, message }): the charge SUCCEEDED but the order could not be saved, so a
// retry is a guaranteed second charge. Checkout renders it inline and blocks further
// attempts (#156). The three 402 payment codes live in @models/paymentMethod.
export const ORDER_SAVE_FAILED = "order_save_failed";

// The returned-payment fee's twin of ORDER_SAVE_FAILED (POST
// /orders/{orderId}/returned-payment-fee, 500): the card WAS charged but the fee
// could not be saved on the order, so the order still offers the charge and a
// second one would collect the fee twice (C-09).
export const FEE_SAVE_FAILED = "fee_save_failed";

// Machine-readable `error` code of POST /orders' coded 409 ({ error, message }, PR 339):
// the stock moved while this cart was being checked out. Nothing was charged and the cart
// is as it was. The other 409 ("This cart is already being checked out.") is a plain string.
export const STOCK_CHANGED_DURING_CHECKOUT = "stock_changed_during_checkout";

// One cart line POST /orders' stock 412 names. `available` is the offering's stock as
// checkout read it; lines of one offering (one `vendorSkuId`) share it.
export interface StockShortfallItem {
    shoppingCartItemId: string;
    vendorSkuId: string;
    productName: string;
    available: number;
}

const isShortfallItem = (value: unknown): value is StockShortfallItem => {
    if (typeof value !== "object" || value === null) {
        return false;
    }

    const { shoppingCartItemId, vendorSkuId, productName, available } =
        value as Record<string, unknown>;

    return (
        typeof shoppingCartItemId === "string" &&
        typeof vendorSkuId === "string" &&
        typeof productName === "string" &&
        typeof available === "number" &&
        Number.isInteger(available) &&
        available >= 0
    );
};

/**
 * The cart lines POST /orders' stock 412 names (#443 / C-07), or undefined for any other
 * body. Refused before anything is charged, and the cart is kept. The one coded 412 —
 * `{ error: "insufficient_stock", message, items }` — so it is keyed on the code, and the
 * lines come from `items`, never out of the sentence. Every other 412 is still a plain
 * string. Takes the raw body (ApiError.detail).
 */
export const stockShortfallItems = (
    body: string | undefined,
): StockShortfallItem[] | undefined => {
    const parsed = parseJsonObject(body);

    if (parsed?.error !== INSUFFICIENT_STOCK || !Array.isArray(parsed.items)) {
        return undefined;
    }

    const items: unknown[] = parsed.items;

    return items.length > 0 && items.every(isShortfallItem)
        ? (items as StockShortfallItem[])
        : undefined;
};

export interface OrderVariantResponse {
    variantId: string;
    skuId: string;
    name: string;
    cost: number;
    price: number;
    msrp: number;
    quantity: number;
}

export interface OrderProductResponse {
    orderId: string;
    productId: string;
    skuId: string;
    name: string;
    upc: string;
    cost: number;
    price: number;
    msrp: number;
    quantity: number;
    // The pieces in one unit the line was placed with (Orders 6), as the line stored
    // it (GetMeOrder's OrderProductResponse.PiecesPerUnit), never the live catalog's. 0 is a
    // line the app placed before API PR 435 deployed (between the migration and the
    // deploy); absent is an API from before this field. Both read as unknown.
    piecesPerUnit?: number;
    // The line's product image as the order read sends it (GetMeOrder's
    // OrderProductResponse.ImageUrl): null when the product has no image. The
    // confirmation renders it directly — no per-line /products/{id} read.
    imageUrl: string | null;
    variants: OrderVariantResponse[];
}

// Mirrors the API's OrderStatusType (Domain/Types/OrderStatusType.cs) after the
// E6-S2 trim. The enum names double as the customer-facing status verbs.
export enum OrderStatusType {
    None = 0,
    Received = 1,
    Processing = 2,
    Shipped = 3,
    Delivered = 4,
    Cancelled = 5,
}

// Mirrors the API's OrderPaymentStatusType (Domain/Types/OrderPaymentStatusType.cs) —
// CONFIRMED against API main @ 386f687 (PR 242), member-for-member. An eCheck-paid order
// sits at PendingSettlement until the daily settlement job resolves it (Captured on
// settlement, Failed on return); amountPaid is null while at 5. Numeric: this API
// serializes enums as integers.
//
// The SINGLE declaration for both the customer and admin surfaces — see the
// PaymentStatusType alias below.
export enum OrderPaymentStatusType {
    None = 0,
    Unpaid = 1,
    AuthOnly = 2,
    Captured = 3,
    Failed = 4,
    PendingSettlement = 5,
}

// Pre-existing name for the same API enum, kept so the admin order surfaces
// (Admin/Orders/_components/status.ts, OrderDetail.tsx) keep compiling unchanged. It is an
// ALIAS, not a copy: one declaration means a renumbering can't be half-applied, and a value
// produced on one surface always `===` the same member on the other.
export { OrderPaymentStatusType as PaymentStatusType };

// The customer-facing pending-settlement copy lives here, next to the enum that
// triggers it, so the confirmation page and the order-history card render the same
// promise and can't drift apart (#296 / §1.11: ACH orders hold until settled).
export const PENDING_SETTLEMENT_NOTICE =
    "Your order will ship once your bank payment clears (typically 3–5 business days).";
export const PENDING_SETTLEMENT_COMPACT_NOTE =
    "Awaiting bank payment — ships once it clears (3–5 business days).";

// #445 / audit C-11 (DecisionLog §3.13 Q4, Q9): the order-card and confirmation
// notes for a payment that failed, kept here for the same no-drift reason. A
// returned bank payment gets its own note (the suspending case, which also gets
// the "Payment Returned" page); any other Failed payment (ACH that never
// settled, a declined or voided card) gets the second. Neither links to the
// page: a returned order stays returned after the hold lifts.
export const PAYMENT_RETURNED_NOTE =
    "Your bank returned the payment for this order.";
export const PAYMENT_FAILED_NOTE =
    "The payment for this order didn't go through.";

// Which of the two notes an order gets, or null for none. The returned flag is
// checked first: a returned payment is also Failed, and it must read as returned.
export const paymentFailureNote = (order: {
    paymentStatus?: number | null;
    isPaymentReturned?: boolean;
}): string | null => {
    if (order.isPaymentReturned) return PAYMENT_RETURNED_NOTE;
    if (order.paymentStatus === OrderPaymentStatusType.Failed) {
        return PAYMENT_FAILED_NOTE;
    }
    return null;
};

// Thumbnail-sized product summary on the GET /orders (me-scoped) list payload.
export interface GetMeOrdersProductResponse {
    productId: string;
    name: string;
    imageUrl: string | null;
    quantity: number;
}

// List item shape for GET /orders (me-scoped).
export interface GetMeOrdersResponse {
    id: string;
    orderNumber: number;
    // Customer-facing "XXXX-XXXX" rendering, computed server-side (E6-S1).
    // Optional because payloads from an API deployed before it landed lack it —
    // the card falls back to client-side formatting then.
    orderNumberFormatted?: string;
    userId: string;
    userName: string;
    userEmail: string;
    status: number;
    currencyCode: string;
    total: number;
    createdAt: string;
    // AO-15: when the order reached its current status — the latest
    // status-history entry, or createdAt when there is none. Optional: absent
    // on an API before audit batch 4; the card then labels createdAt as the
    // placement date instead.
    statusChangedAt?: string;
    locationId: string;
    locationName: string;
    // The three summary fields below are optional because payloads from an
    // API deployed before the #289 enrichment legitimately lack them — the
    // card renders defensively (0 items, no thumbnail row) in that case.
    // Sum of ALL line quantities on the order.
    totalItemCount?: number;
    // The first up to 4 line items, for the dashboard thumbnail row.
    products?: GetMeOrdersProductResponse[];
    // Remaining item quantity beyond `products` (0 when none) — the "+N More" value.
    moreItemCount?: number;
    // OrderPaymentStatusType of the order's payment. Optional/nullable because payloads
    // from an API deployed before #296 legitimately lack it — the card shows no payment
    // note then. CONFIRMED, as the enum above.
    paymentStatus?: number | null;
    // #445: the order's BANK payment was returned (Failed, nothing paid, eCheck).
    // Optional: absent on an API before PR 379, read as false.
    isPaymentReturned?: boolean;
}

// The invoice issued for an order (E7 #214/#216), as the order reads expose it.
// Issuing is best-effort — it can fail without failing the order — so an order
// legitimately has no invoice: the reads send `null` then. #217 sets `voidedAt`;
// a voided invoice is STILL downloadable (the API names the file
// `<invoiceNumber>-VOID.pdf`) and the UI marks it with a VOIDED badge.
// Mirrors the API's OrderInvoiceResponse (PR 278).
export interface OrderInvoiceSummary {
    id: string;
    invoiceNumber: string;
    voidedAt: string | null;
}

export interface GetMeOrderResponse {
    id: string;
    // Sequential human order number + its XXXX-XXXX display form. Always sent:
    // OrderNumberFormatted is a computed property on the API's GetMeOrderResponse.
    orderNumber: number;
    orderNumberFormatted: string;
    userId: string;
    userName: string;
    userEmail: string;
    status: number;
    createdAt: string;
    products: OrderProductResponse[];
    locationId: string;
    locationName: string;
    total: number;
    // Server-computed money breakdown persisted with the order (#176/#316):
    // total = subtotal + shippingCost. Optional because payloads from an API
    // deployed before these fields landed legitimately omit them — the
    // confirmation falls back to total-only rendering then.
    subtotal?: number;
    shippingCost?: number;
    // Masked payment summary persisted with the order. Optional/nullable: the
    // API returns `string | null`, and payloads from before these fields landed
    // legitimately omit them — the confirmation page masks defensively then.
    // CONFIRMED (#296): reads "eCheck" for ACH orders.
    paymentMethodBrand?: string | null;
    paymentMethodLast4?: string | null;
    // OrderPaymentStatusType of the order's payment. Optional/nullable because payloads
    // from an API deployed before #296 legitimately lack it — the confirmation shows no
    // settlement notice then. CONFIRMED, as the enum above.
    paymentStatus?: number | null;
    // #445: as on GetMeOrdersResponse.
    isPaymentReturned?: boolean;
    // Card processing fee persisted with the order (#297), already included in `total`
    // — never re-add client-side. CONFIRMED, as the enum above: a plain decimal, 0
    // (not null) for ACH/legacy orders; optional here only because pre-deploy payloads
    // lack it. The confirmation renders the fee row only when present and > 0.
    // (The order detail also gained a `paymentMethod` int — deliberately NOT mirrored
    // here: no customer surface consumes it.)
    surchargeAmount?: number | null;
    // Always written by the order reads: null when the API says no invoice was
    // issued (the admin reads a sentence; the customer nothing — an ACH order's
    // invoice follows settlement).
    invoice: OrderInvoiceSummary | null;
}

export enum GetOrdersSearchOptionType {
    None = 0,
    OrderNumber = 1,
    OrganizationName = 2,
    TrackingNumber = 3,
}

export enum GetOrdersSortOptionType {
    None = 0,
    Id = 1,
    OrderNumber = 2,
    Status = 3,
    Total = 4,
    CreatedAt = 5,
    LastUpdatedAt = 6,
    // API PR 416 (ruling O5): the Customer and Location columns.
    OrganizationName = 7,
    LocationName = 8,
}

export enum OrdersSearchType {
    None = 0,
    Contains = 1,
    Equals = 2,
    StartsWith = 3,
    EndsWith = 4,
}

export enum OrdersSortDirection {
    None = 0,
    Asc = 1,
    Desc = 2,
}

export interface AdminOrderListItem {
    id: string;
    orderNumber: number;
    orderNumberFormatted?: string;
    userId: string;
    userName: string;
    userEmail: string;
    status: OrderStatusType;
    total: number;
    createdAt: string;
    locationId: string;
    locationName: string;
    organizationId: string;
    organizationName: string;
    lastUpdatedAt: string | null;
    // Sent by GET /orders since API PRs 111 (payment) and 165 (ship-to,
    // tracking); declared for the list's bulk actions (#473): Mark Processed
    // skips an order whose payment is not Captured, and Export writes them.
    // OrderPaymentStatusType as an integer; null when the order has none.
    paymentStatus: number | null;
    shippingStreetAddress: string;
    shippingCity: string;
    shippingState: string;
    shippingZipCode: string;
    trackingNumber: string | null;
    carrier: string | null;
}

export interface PaginatedOrders {
    pageNumber: number;
    totalPages: number;
    // Rows matching the filter.
    count: number;
    // TODO(backend): allCount, the rows before any filter (PO ruling X4). Until
    // the API sends it the admin list's total reads "{count} results".
    allCount?: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    items: AdminOrderListItem[];
}

export interface OrderStatusCounts {
    total: number;
    received: number;
    processing: number;
    shipped: number;
    delivered: number;
    cancelled: number;
}

export interface GetOrdersQuery {
    pageNumber?: number;
    pageSize?: number;
    search?: string;
    searchType?: OrdersSearchType;
    searchBy?: GetOrdersSearchOptionType[];
    sortBy?: GetOrdersSortOptionType[];
    sortDirection?: OrdersSortDirection;
    // Omit (leave undefined) for "All"; None=0 is never sent as a filter.
    status?: OrderStatusType;
    // Independent Contains filter, distinct from the `search` param.
    trackingNumber?: string;
}

export interface AdminOrderVariantOptionValue {
    optionName: string;
    value: string;
}

export interface AdminOrderVariant {
    variantId: string;
    name: string;
    optionValues: AdminOrderVariantOptionValue[];
}

export interface AdminOrderProduct {
    orderId: string;
    productId: string;
    skuId: string;
    variantId: string | null;
    name: string;
    upc: string;
    description: string | null;
    brand: string | null;
    imageUrl: string | null;
    cost: number;
    price: number;
    msrp: number;
    quantity: number;
    // Orders 6 (API PR 435): the pieces per unit the line recorded at placement,
    // never the catalogue's value today. 0 for a line the app placed before PR
    // 435 deployed, between the migration and the deploy (the old app's INSERT
    // takes the column's default); absent on an API from before PR 435. Both
    // read as unknown (utilities/recordedPiecesPerUnit).
    piecesPerUnit?: number;
    legalClasses: string[];
    variant: AdminOrderVariant | null;
}

export interface AdminOrderStatusHistoryItem {
    id: string;
    fromStatus: OrderStatusType | null;
    toStatus: OrderStatusType;
    changedAt: string;
    changedByUserId: string | null;
    // AO-16 (API PR 399): who made the change, "First Last"; null when the
    // row records no actor. The system actor's name is "System Actor".
    // Optional: an API from before PR 399 omits it.
    changedByUserName?: string | null;
    notes: string | null;
}

// Integer values of AdminOrderDetailResponse.paymentStatus (#298 adds PendingSettlement
// for ACH — eCheck charges settle days after submission) and .paymentMethod (#298). Both
// are declared once and re-exported here, NOT re-declared: see OrderPaymentStatusType
// above and @models/paymentMethod. The fields themselves stay `number | null` (their
// pre-enum shape); the enums exist for named comparisons and labeling.
export { PaymentMethodType };

export interface AdminOrderDetailResponse {
    id: string;
    orderNumber: number;
    orderNumberFormatted: string;
    userId: string;
    userName: string;
    userEmail: string;
    status: OrderStatusType;
    createdAt: string;
    lastUpdatedAt: string | null;
    locationId: string;
    locationName: string;
    organizationId: string;
    organizationName: string;
    currencyCode: string;
    subtotal: number;
    tax: number;
    shippingCost: number;
    discountTotal: number;
    total: number;
    paymentTransactionId: string | null;
    paymentStatus: number | null;
    amountPaid: number | null;
    paymentMethodBrand: string | null;
    paymentMethodLast4: string | null;
    shippingStreetAddress: string;
    shippingCity: string;
    shippingState: string;
    shippingZipCode: string;
    shippingCountry: string;
    trackingNumber: string | null;
    carrier: string | null;
    products: AdminOrderProduct[];
    statusHistory: AdminOrderStatusHistoryItem[];
    // #298 ACH additions. Optional because payloads from an API deployed before
    // they landed legitimately omit them — the page renders defensively (and
    // hides the charge-fee action, whose endpoint would not exist either).
    // Card surcharge included in `total` (2.9% card orders; ACH is free).
    surchargeAmount?: number;
    // Nullable defensively: paymentStatus is already `| null`, and a null here
    // must render "—", never the literal "null".
    paymentMethod?: PaymentMethodType | null;
    // Returned-payment (NSF) fee, recorded once charged via the admin action.
    returnedPaymentFeeAmount?: number | null;
    returnedPaymentFeeTransactionId?: string | null;
    returnedPaymentFeeChargedAt?: string | null;
    // #469: set while a fee charge on this order is unconfirmed (its claim
    // stands), until an admin resolves it on Billing › Unconfirmed Charges.
    returnedPaymentFeeUnconfirmedAt?: string | null;
    // Always written, nullable — same reading as on GetMeOrderResponse.
    invoice: OrderInvoiceSummary | null;
}

// POST /orders/{orderId}/returned-payment-fee (#298, admin). Failure contract,
// every body { error, message, transactionId? }:
// - 402 payment_declined, payment_unavailable, payment_unconfirmed and
//   payment_held (the codes in @models/paymentMethod; held carries the id);
// - 409 payment_not_returned, fee_already_charged, fee_unconfirmed (#469: an
//   earlier charge is unconfirmed), no_vendor_profile, no_active_card, and
//   charge_not_recorded (#469: this charge went through but is not recorded;
//   carries the id);
// - 500 fee_save_failed (charged but not saved; carries the id).
// All render inline in the charge dialog — see
// Features/Admin/Orders/_helpers/returnedPaymentFeeError.ts.
export interface ChargeReturnedPaymentFeeRequest {
    amount: number;
}

export interface ChargeReturnedPaymentFeeResponse {
    amount: number;
    transactionId: string;
    chargedAt: string;
}

// POST /orders/{orderId}/payment-return (C-10, admin, no body): records a bank
// return the ACH poller missed. The payment becomes Failed (4) with 0 paid and
// the organization is suspended (organizationStatus 1). Failure contract:
// 404 unknown order; 409 { error, message } with not_bank_payment,
// payment_not_captured or order_cancelled — rendered inline, see
// Features/Admin/Orders/_helpers/paymentReturnRefusal.ts.
export interface RecordPaymentReturnResponse {
    orderId: string;
    paymentStatus: number;
    amountPaid: number;
    organizationStatus: number;
}

export interface TransitionOrderRequest {
    toStatus: OrderStatusType;
    trackingNumber?: string;
    carrier?: string;
    notes?: string;
}

export const DEFAULT_ORDERS_QUERY: GetOrdersQuery = {
    pageNumber: 1,
    pageSize: 25,
    search: "",
    searchType: OrdersSearchType.Contains,
    // The Orders page's single search box matches across all three fields.
    searchBy: [
        GetOrdersSearchOptionType.OrderNumber,
        GetOrdersSearchOptionType.OrganizationName,
        GetOrdersSearchOptionType.TrackingNumber,
    ],
    sortBy: [GetOrdersSortOptionType.CreatedAt],
    sortDirection: OrdersSortDirection.Desc,
};
